import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, ShoppingCart, Upload, X, Eye, Calendar, Filter } from "lucide-react";
import axios from "axios";
import ImportExportButtons from "../../components/ImportExportButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date as dd/mm/yyyy
const formatDateDMY = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function PurchasingTab({ user = null }) {
  const [purchases, setPurchases] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editMechanicSearch, setEditMechanicSearch] = useState("");
  const [editSupplierSearch, setEditSupplierSearch] = useState("");
  const [viewingInvoice, setViewingInvoice] = useState(null);
  
  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("all");
  
  // New purchase state - simplified
  const [newPurchase, setNewPurchase] = useState({
    date: new Date().toISOString().split('T')[0],
    mechanic_id: "",
    supplier_id: "",
    total_price: 0,
    invoice_image: "",
    mechanicSearch: ""
  });
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Auto-set mechanic if logged-in user is a mechanic
  useEffect(() => {
    if (user?.role === 'mechanic' && mechanics.length > 0) {
      const loggedInMechanic = mechanics.find(m => m.id === user.id || m.username === user.username);
      if (loggedInMechanic) {
        setNewPurchase(prev => ({
          ...prev,
          mechanic_id: loggedInMechanic.id,
          mechanicSearch: loggedInMechanic.name || loggedInMechanic.full_name
        }));
      }
    }
  }, [user, mechanics]);

  const fetchAllData = async () => {
    try {
      const [purchasesRes, mechanicsRes, inventoryRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/purchases`),
        axios.get(`${API}/mechanics`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/suppliers`)
      ]);
      setPurchases(purchasesRes.data || []);
      setMechanics(mechanicsRes.data || []);
      setInventory(inventoryRes.data || []);
      setSuppliers(suppliersRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load purchasing data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async () => {
    try {
      const res = await axios.get(`${API}/purchases`);
      setPurchases(res.data || []);
    } catch (error) {
      console.error("Error fetching purchases:", error);
    }
  };

  // Filter purchases
  const filteredPurchases = purchases.filter(p => {
    const matchesSearch = p.mechanic_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSupplier = supplierFilter === "all" || p.supplier_id === supplierFilter;
    
    let matchesDate = true;
    if (dateFrom) {
      matchesDate = matchesDate && p.date >= dateFrom;
    }
    if (dateTo) {
      matchesDate = matchesDate && p.date <= dateTo;
    }
    
    return matchesSearch && matchesSupplier && matchesDate;
  });

  // Filtered lists for edit modal autocomplete
  const filteredEditMechanics = mechanics.filter(m =>
    (m.name || m.full_name)?.toLowerCase().includes(editMechanicSearch.toLowerCase())
  );
  const filteredEditSuppliers = (suppliers || []).filter(s =>
    s.name?.toLowerCase().includes(editSupplierSearch.toLowerCase())
  );

  // Filter mechanics for new purchase autocomplete
  const filteredMechanics = mechanics.filter(m =>
    (m.name || m.full_name)?.toLowerCase().includes(newPurchase.mechanicSearch.toLowerCase())
  );

  const handleImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (isEdit) {
        setEditingPurchase({ ...editingPurchase, invoice_image: event.target.result });
      } else {
        setNewPurchase({ ...newPurchase, invoice_image: event.target.result });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newPurchase.invoice_image) {
      toast.error("Please upload an invoice image");
      return;
    }
    
    if (!newPurchase.supplier_id) {
      toast.error("Please select a supplier");
      return;
    }
    
    if (!newPurchase.mechanic_id) {
      toast.error("Please select a mechanic");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/purchases`, {
        date: newPurchase.date,
        mechanic_id: newPurchase.mechanic_id,
        supplier_id: newPurchase.supplier_id,
        item_id: null, // No longer required
        quantity: 1, // Default
        unit_price: parseFloat(newPurchase.total_price), // Total price stored as unit_price
        invoice_number: "",
        invoice_image: newPurchase.invoice_image,
        notes: ""
      });
      toast.success("Purchase created successfully!");
      setShowAddModal(false);
      // Reset form but keep mechanic if user is a mechanic
      const resetMechanic = user?.role === 'mechanic' ? {
        mechanic_id: newPurchase.mechanic_id,
        mechanicSearch: newPurchase.mechanicSearch
      } : { mechanic_id: "", mechanicSearch: "" };
      setNewPurchase({
        date: new Date().toISOString().split('T')[0],
        ...resetMechanic,
        supplier_id: "",
        total_price: 0,
        invoice_image: ""
      });
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create purchase");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/admin/purchases/${editingPurchase.id}`, {
        date: editingPurchase.date,
        mechanic_id: editingPurchase.mechanic_id,
        supplier_id: editingPurchase.supplier_id,
        quantity: 1,
        unit_price: parseFloat(editingPurchase.total_cost || editingPurchase.unit_price),
        invoice_image: editingPurchase.invoice_image
      });
      toast.success("Purchase updated successfully!");
      setEditingPurchase(null);
      setEditMechanicSearch("");
      setEditSupplierSearch("");
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update purchase");
    }
  };

  const handleDelete = async (purchaseId) => {
    if (!window.confirm("Are you sure you want to delete this purchase?")) return;
    try {
      await axios.delete(`${API}/admin/purchases/${purchaseId}`);
      toast.success("Purchase deleted successfully!");
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete purchase");
    }
  };

  const selectMechanic = (mechanic) => {
    setNewPurchase({
      ...newPurchase,
      mechanic_id: mechanic.id,
      mechanicSearch: mechanic.name || mechanic.full_name
    });
  };

  const selectSupplier = (supplier) => {
    setNewPurchase({
      ...newPurchase,
      supplier_id: supplier.id
    });
  };

  // Selection handlers for edit modal
  const selectEditMechanic = (mechanic) => {
    setEditingPurchase({
      ...editingPurchase,
      mechanic_id: mechanic.id,
      mechanic_name: mechanic.name || mechanic.full_name
    });
    setEditMechanicSearch("");
  };

  const selectEditSupplier = (supplier) => {
    setEditingPurchase({
      ...editingPurchase,
      supplier_id: supplier.id,
      supplier_name: supplier.name
    });
    setEditSupplierSearch("");
  };

  const totalSpent = filteredPurchases.reduce((sum, p) => sum + (p.total_cost || 0), 0);

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSupplierFilter("all");
    setSearchTerm("");
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
              Purchasing
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {filteredPurchases.length} purchases | Total: ${totalSpent.toFixed(2)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <ImportExportButtons
              entityName="Purchases"
              exportEndpoint="/admin/purchases/export"
              importEndpoint="/admin/purchases/import"
              templateEndpoint="/admin/purchases/template"
              onImportSuccess={fetchPurchases}
              exportFileName="purchases"
            />
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap flex-1 sm:flex-none"
              data-testid="add-purchase-btn"
            >
              <Plus className="w-4 h-4" />
              <span>Add Purchase</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Search by mechanic or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500"
            data-testid="purchase-search"
          />
        </div>
        
        {/* Filter Row */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Calendar className="w-4 h-4 text-white/40 hidden sm:block" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="From"
              data-testid="date-from-filter"
            />
            <span className="text-white/40">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="To"
              data-testid="date-to-filter"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/40 hidden sm:block" />
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="supplier-filter"
            >
              <option value="all">All Suppliers</option>
              {(suppliers || []).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {(dateFrom || dateTo || supplierFilter !== "all" || searchTerm) && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 bg-white/10 text-white/60 rounded-lg hover:bg-white/20 text-sm"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Purchases Table - Simplified columns */}
      <div className="bg-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="purchases-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/60 font-medium">Date</th>
                <th className="text-left p-4 text-white/60 font-medium">Mechanic</th>
                <th className="text-left p-4 text-white/60 font-medium">Supplier</th>
                <th className="text-right p-4 text-white/60 font-medium">Total</th>
                <th className="text-center p-4 text-white/60 font-medium">Invoice</th>
                <th className="text-right p-4 text-white/60 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.map((purchase) => (
                <tr key={purchase.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 text-white">{formatDateDMY(purchase.date)}</td>
                  <td className="p-4 text-white/80">{purchase.mechanic_name}</td>
                  <td className="p-4 text-teal-400">{purchase.supplier_name || "-"}</td>
                  <td className="p-4 text-right text-green-400 font-semibold">${purchase.total_cost?.toFixed(2)}</td>
                  <td className="p-4 text-center">
                    {purchase.invoice_image ? (
                      <button
                        onClick={() => setViewingInvoice(purchase)}
                        className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                        data-testid={`view-invoice-${purchase.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="text-white/30">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingPurchase({ ...purchase });
                          setEditMechanicSearch("");
                          setEditSupplierSearch("");
                        }}
                        className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                        data-testid={`edit-purchase-${purchase.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(purchase.id)}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                        data-testid={`delete-purchase-${purchase.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPurchases.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-white/40">
                    No purchases found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal - Simplified */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Add Purchase</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Date *</label>
                <input
                  type="date"
                  value={newPurchase.date}
                  onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                  data-testid="new-purchase-date"
                />
              </div>
              
              {/* Mechanic Autocomplete */}
              <div className="relative">
                <label className="block text-white/60 text-sm mb-1">Mechanic *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPurchase.mechanicSearch}
                    onChange={(e) => setNewPurchase({ ...newPurchase, mechanicSearch: e.target.value, mechanic_id: "" })}
                    placeholder="Search mechanic..."
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    required={!newPurchase.mechanic_id}
                    data-testid="new-purchase-mechanic-search"
                  />
                  {newPurchase.mechanic_id && (
                    <button
                      type="button"
                      onClick={() => setNewPurchase({ ...newPurchase, mechanic_id: "", mechanicSearch: "" })}
                      className="p-2 text-white/60 hover:text-white"
                    >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    {newPurchase.mechanicSearch && !newPurchase.mechanic_id && filteredMechanics.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredMechanics.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => selectMechanic(m)}
                            className="w-full text-left px-3 py-2 text-white hover:bg-white/10"
                          >
                            {m.name || m.full_name}
                          </button>
                        ))}
                      </div>
                    )}
              </div>

              {/* Supplier Selection - Display all suppliers */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Select Supplier *</label>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto p-2 bg-white/5 border border-white/10 rounded-lg">
                  {(suppliers || []).length === 0 ? (
                    <p className="text-white/40 text-center py-4">No suppliers available. Please add suppliers first.</p>
                  ) : (
                    (suppliers || []).map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => selectSupplier(s)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          newPurchase.supplier_id === s.id
                            ? "bg-green-500 text-white"
                            : "bg-white/5 text-white hover:bg-white/10"
                        }`}
                        data-testid={`select-supplier-${s.id}`}
                      >
                        <div className="font-medium">{s.name}</div>
                        {s.contact_person && (
                          <div className="text-xs opacity-70">{s.contact_person}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Total Invoice Price ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPurchase.total_price}
                  onChange={(e) => setNewPurchase({ ...newPurchase, total_price: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  min="0"
                  required
                  placeholder="Enter total invoice amount"
                  data-testid="new-purchase-total-price"
                />
              </div>

              {/* Invoice Image Upload */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Invoice Image *</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleImageUpload(e, false)}
                  accept="image/*"
                  className="hidden"
                  data-testid="new-purchase-invoice-input"
                />
                {newPurchase.invoice_image ? (
                  <div className="relative">
                    <img
                      src={newPurchase.invoice_image}
                      alt="Invoice preview"
                      className="w-full h-40 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => setNewPurchase({ ...newPurchase, invoice_image: "" })}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full p-4 border-2 border-dashed border-white/20 rounded-lg text-white/60 hover:border-green-500 hover:text-green-400 transition-colors flex items-center justify-center gap-2"
                    data-testid="upload-invoice-btn"
                  >
                    <Upload className="w-5 h-5" />
                    Upload Invoice Image
                  </button>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  data-testid="submit-new-purchase"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Simplified */}
      {editingPurchase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Edit Purchase</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-1">Date</label>
                <input
                  type="date"
                  value={editingPurchase.date}
                  onChange={(e) => setEditingPurchase({ ...editingPurchase, date: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="edit-purchase-date"
                />
              </div>

              {/* Mechanic Autocomplete */}
              <div className="relative">
                <label className="block text-white/60 text-sm mb-1">Mechanic</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editMechanicSearch || editingPurchase.mechanic_name}
                    onChange={(e) => setEditMechanicSearch(e.target.value)}
                    placeholder="Search mechanic..."
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="edit-purchase-mechanic-search"
                  />
                  {editMechanicSearch && (
                    <button
                      type="button"
                      onClick={() => setEditMechanicSearch("")}
                      className="p-2 text-white/60 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {editMechanicSearch && filteredEditMechanics.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredEditMechanics.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => selectEditMechanic(m)}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10"
                      >
                        {m.name || m.full_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Supplier Autocomplete */}
              <div className="relative">
                <label className="block text-white/60 text-sm mb-1">Supplier</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editSupplierSearch || editingPurchase.supplier_name || ""}
                    onChange={(e) => setEditSupplierSearch(e.target.value)}
                    placeholder="Search supplier..."
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="edit-purchase-supplier-search"
                  />
                  {editSupplierSearch && (
                    <button
                      type="button"
                      onClick={() => setEditSupplierSearch("")}
                      className="p-2 text-white/60 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {editSupplierSearch && filteredEditSuppliers.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-zinc-800 border border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredEditSuppliers.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => selectEditSupplier(s)}
                        className="w-full text-left px-3 py-2 text-white hover:bg-white/10"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-1">Total Invoice Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingPurchase.total_cost || editingPurchase.unit_price || 0}
                  onChange={(e) => setEditingPurchase({ ...editingPurchase, total_cost: e.target.value })}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  min="0"
                  data-testid="edit-purchase-total-price"
                />
              </div>

              {/* Invoice Image */}
              <div>
                <label className="block text-white/60 text-sm mb-1">Invoice Image</label>
                <input
                  type="file"
                  ref={editFileInputRef}
                  onChange={(e) => handleImageUpload(e, true)}
                  accept="image/*"
                  className="hidden"
                />
                {editingPurchase.invoice_image ? (
                  <div className="relative">
                    <img
                      src={editingPurchase.invoice_image}
                      alt="Invoice"
                      className="w-full h-40 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => editFileInputRef.current?.click()}
                      className="absolute bottom-2 right-2 px-3 py-1 bg-blue-500 text-white text-sm rounded-lg"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => editFileInputRef.current?.click()}
                    className="w-full p-4 border-2 border-dashed border-white/20 rounded-lg text-white/60 hover:border-green-500 hover:text-green-400 transition-colors flex items-center justify-center gap-2"
                  >
                    <Upload className="w-5 h-5" />
                    Upload Invoice Image
                  </button>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingPurchase(null);
                    setEditMechanicSearch("");
                    setEditSupplierSearch("");
                  }}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  data-testid="submit-edit-purchase"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewingInvoice && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setViewingInvoice(null)}>
          <div className="bg-zinc-900 rounded-xl p-4 max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-bold text-white">Invoice</h3>
                <p className="text-white/60 text-sm">{viewingInvoice.supplier_name} - {formatDateDMY(viewingInvoice.date)}</p>
              </div>
              <button
                onClick={() => setViewingInvoice(null)}
                className="p-2 text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <img
              src={viewingInvoice.invoice_image}
              alt="Invoice"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
}
